import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { CallChatMessage, CallParticipant } from '../../types';
import { splitByUrl } from '../../utils/linkify';
import { openExternalUrl } from '../../utils/openExternal';
import { isTauri } from '../../utils/tauri';
import { IS_COARSE_POINTER } from '../../utils/pointer';
import { CALL_CHAT_MAX_CHARS } from '../../services/callController';

/**
 * 통화 채팅 — 통화 참가자 전원(게스트 포함)이 보는 통화 전용 대화(2026-11-04).
 * 대화방 채팅(ChatWindow)과 달리 서버 메모리에만 있고 통화가 끝나면 사라진다. 첨부·멘션·답장·리액션은 없다.
 * 기록은 callController의 별도 스토어가 들고 있고(VideoCallWindow가 구독해 넘긴다) 이 컴포넌트는 그리기·입력만 한다.
 */
interface CallChatPanelProps {
  messages: CallChatMessage[];
  currentUserId: string;
  /** 통화 채팅을 못 보는 참가자(구버전 앱) */
  outdated: CallParticipant[];
  /** 보내기 — false면 입력한 글을 지우지 않는다 */
  onSend: (text: string) => Promise<boolean>;
}

// 같은 사람이 5분 안에 이어 쓴 글은 이름 행을 생략한다(대화방 메시지 그룹핑과 같은 기준)
const CONTINUATION_MS = 5 * 60 * 1000;
// 바닥에서 이만큼 안쪽이면 '바닥을 보고 있다' — 새 글이 오면 따라 내려간다
const NEAR_BOTTOM_PX = 80;

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit' });
}

function outdatedNotice(list: CallParticipant[]): string | null {
  if (list.length === 0) return null;
  const who = list.length === 1 ? `${list[0].userName}님은` : `${list[0].userName}님 외 ${list.length - 1}명은`;
  return `${who} 앱을 업데이트해야 통화 채팅을 볼 수 있습니다`;
}

const CallChatPanel: React.FC<CallChatPanelProps> = ({ messages, currentUserId, outdated, onSend }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nearBottomRef = useRef(true);
  const lastCountRef = useRef(0);

  // 새 글: 바닥을 보고 있었거나 내가 쓴 글이면 바닥으로. 처음 그릴 때도 바닥에서 시작한다
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const grew = messages.length > lastCountRef.current;
    const mine = messages.length > 0 && messages[messages.length - 1].userId === currentUserId;
    if (lastCountRef.current === 0 || (grew && (nearBottomRef.current || mine))) {
      el.scrollTop = el.scrollHeight;
    }
    lastCountRef.current = messages.length;
  }, [messages, currentUserId]);

  // 데스크톱은 패널을 열면 바로 쓸 수 있게(터치 기기는 키보드가 화면을 덮으므로 포커스하지 않는다)
  useEffect(() => {
    if (!IS_COARSE_POINTER) inputRef.current?.focus();
  }, []);

  // 입력창 높이를 글에 맞춘다(최대 높이는 CSS max-h)
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  const submit = useCallback(async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    const ok = await onSend(body);
    setSending(false);
    // 보내는 사이 더 쓴 글은 지우지 않는다 — 보낸 글 그대로일 때만 비운다
    if (ok) setText(prev => (prev.trim() === body ? '' : prev));
    if (!IS_COARSE_POINTER) inputRef.current?.focus();
  }, [text, sending, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    // 한글 마지막 음절을 확정하는 Enter는 보내지 않는다
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    // 터치 기기는 Enter = 줄바꿈(전송은 버튼) — 대화방 입력창과 같은 규칙
    if (IS_COARSE_POINTER) return;
    e.preventDefault();
    void submit();
  };

  const renderText = (body: string, isOwn: boolean) => splitByUrl(body).map((seg, i) => (
    seg.type === 'url' ? (
      <a
        key={i}
        href={seg.value}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          // 데스크톱은 기본 브라우저로 — WebView 안에서 열리면 돌아올 방법이 없다
          if (isTauri()) {
            e.preventDefault();
            void openExternalUrl(seg.value);
          }
        }}
        className={`underline break-all ${isOwn ? 'text-brand-700 hover:text-brand-800' : 'text-brand-400 hover:text-brand-300'}`}
      >
        {seg.value}
      </a>
    ) : (
      <React.Fragment key={i}>{seg.value}</React.Fragment>
    )
  ));

  const notice = outdatedNotice(outdated);

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-gray-950">
      <div className="px-4 py-2 border-b border-gray-800 text-xs text-gray-400 space-y-1">
        <p>통화에 참여한 사람 모두가 봅니다. 통화가 끝나면 사라집니다.</p>
        {notice && <p className="text-gray-300">{notice}</p>}
      </div>

      <div
        ref={listRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-label="통화 채팅"
        className="flex-1 min-h-0 overflow-y-auto px-4 py-3"
      >
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <p className="text-gray-300 text-sm font-medium">아직 통화 채팅이 없습니다</p>
            <p className="text-gray-500 text-xs mt-1">게스트를 포함한 통화 참가자 모두와 대화해 보세요</p>
          </div>
        ) : messages.map((m, i) => {
          const prev = i > 0 ? messages[i - 1] : null;
          const isOwn = m.userId === currentUserId;
          const continuation = !!prev && prev.userId === m.userId
            && new Date(m.at).getTime() - new Date(prev.at).getTime() < CONTINUATION_MS;
          return (
            <div key={m.id} className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} ${continuation ? 'mt-1' : i > 0 ? 'mt-3' : ''}`}>
              {!continuation && (
                <div className={`flex items-center gap-1.5 mb-1 max-w-full ${isOwn ? 'flex-row-reverse' : ''}`}>
                  {!isOwn && <span className="text-gray-200 text-xs font-medium truncate">{m.userName}</span>}
                  {!isOwn && m.guest && (
                    <span className="shrink-0 px-1.5 py-0.5 rounded bg-gray-700 text-gray-200 text-[11px]">게스트</span>
                  )}
                  <span className="shrink-0 text-gray-500 text-[11px]">{formatTime(m.at)}</span>
                </div>
              )}
              <div
                className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                  isOwn ? 'bg-brand-200 text-slate-800' : 'bg-gray-800 text-gray-100'
                }`}
              >
                {renderText(m.text, isOwn)}
              </div>
            </div>
          );
        })}
      </div>

      <form
        className="border-t border-gray-800 p-3 flex items-end gap-2"
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
      >
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          maxLength={CALL_CHAT_MAX_CHARS}
          placeholder="통화 참가자에게 메시지 보내기"
          aria-label="통화 채팅 입력"
          className="flex-1 min-w-0 resize-none max-h-32 overflow-y-auto rounded-xl bg-gray-800 text-gray-100 placeholder-gray-500 text-sm px-3 py-2 outline-none focus:ring-2 focus:ring-gray-600"
        />
        <button
          type="submit"
          disabled={!text.trim() || sending}
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-brand-600 hover:bg-brand-700 text-white disabled:bg-gray-800 disabled:text-gray-500 transition-colors"
          title="보내기"
          aria-label="보내기"
        >
          <Send size={18} />
        </button>
      </form>
    </div>
  );
};

export default CallChatPanel;
